import type { Dispatch, StateUpdater } from 'preact/hooks'
import type { WebViewDto } from '@/api/webs'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { FileText } from 'lucide-preact'
import { getWebTypeMeta } from '@/constants/webTypes'
import { WebApprovalControls } from './WebApprovalControls'
import { SignOffReadinessChip } from './SignOffReadinessChip'
import { LeadsProgressButton } from './LeadsProgressButton'
import { formatEventDateRange } from '@/utils/dateTimeFormatters'
import { useTimeZones } from '@/components/calendar/hooks/useTimeZones'

/** Lazy-loaded broadcast date — only mounts for online events so useTimeZones() doesn't fire on every tab. */
function BroadcastDate({ web }: { web: WebViewDto }) {
    const { getAbbreviation } = useTimeZones()
    const isOnDemand = web.OnlineEvent?.ModeType?.toLowerCase() === 'on-demand (recording only)'
    return (
        <div className="mt-2">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">
                {isOnDemand ? 'Launch Date' : 'Broadcast Date'}
            </div>
            <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {isOnDemand
                    ? formatEventDateRange(web.LaunchDate, undefined, undefined)
                    : formatEventDateRange(
                        web.OnlineEvent?.StartDateTime,
                        web.OnlineEvent?.EndDateTime,
                        web.OnlineEvent?.TimeZone ? getAbbreviation(web.OnlineEvent.TimeZone) : undefined
                    )
                }
            </div>
        </div>
    )
}

interface Props {
    web: WebViewDto
    setWeb: Dispatch<StateUpdater<WebViewDto | null>>
    displayTitle: string
    isOnlineEvent: boolean
    statusTone: string
    workspaceId: string
    campaignId: string
    webId: string
    hasBigMarker: boolean
    integrationError?: boolean
    routeToTab: (tab: WebTabKey) => void
}

export function WebDetailHeader({
    web, setWeb, displayTitle, isOnlineEvent, statusTone,
    workspaceId, campaignId, webId, hasBigMarker, integrationError, routeToTab,
}: Props) {
    const typeMeta = getWebTypeMeta(web.Type)
    const TypeIcon = typeMeta.Icon
    const typeChipClass = typeMeta.chipClass

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
                <div>
                    <h1
                        className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight break-words line-clamp-3 hover:line-clamp-none transition-all duration-200 cursor-pointer max-w-[58ch] md:max-w-[54ch]"
                        title={displayTitle}
                        onClick={event => {
                            const element = event.currentTarget;
                            if (element.classList.contains('line-clamp-3')) {
                                element.classList.remove('line-clamp-3');
                            } else {
                                element.classList.add('line-clamp-3');
                            }
                        }}
                    >
                        {displayTitle}
                    </h1>
                    {isOnlineEvent && <BroadcastDate web={web} />}
                    <div className={`${isOnlineEvent ? 'mt-4' : 'mt-3'} flex flex-wrap items-center gap-2`}>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${typeChipClass}`}><TypeIcon className="w-4 h-4" />{web.Type}</span>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${statusTone}`}><span className="w-2 h-2 rounded-full bg-current opacity-70" />{web.Status}</span>
                        {/* Every web type is signed off — ApprovalStatus defaults on all of them and the
                            readiness rules cover Download, Meeting and Demo titles too. This was gated on
                            isOnlineEvent, which was survivable while those types required a title at
                            creation; now that ORB-459 defers it, that gate would leave a Download web with
                            no way to ever be signed off. The BigMarker push stays online-event-only, which
                            the API already enforces and hasBigMarker below reflects. */}
                        {web.ApprovalStatus && (
                            <>
                                <SignOffReadinessChip outstanding={web.OutstandingForSignOff} routeToTab={routeToTab} />
                                <WebApprovalControls
                                    web={web}
                                    setWeb={setWeb}
                                    workspaceId={workspaceId}
                                    campaignId={campaignId}
                                    webId={webId}
                                    hasBigMarker={hasBigMarker && isOnlineEvent}
                                    integrationError={integrationError && isOnlineEvent}
                                />
                            </>
                        )}
                    </div>
                    {web.IncludedFileImports && web.IncludedFileImports.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400 mr-1">Audiences:</span>
                            {web.IncludedFileImports.map(fileImport => (
                                <span key={fileImport.Id} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"><FileText className="w-3 h-3" />{fileImport.Name}</span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    <LeadsProgressButton
                        leadGoal={web.LeadGoal || 0}
                        leadCount={web.LeadCount || 0}
                        onNavigate={() => routeToTab('leads')}
                    />
                </div>
            </div>
        </div>
    )
}
